'use client';

/**
 * The on-device transaction queue.
 *
 * Internet at a Customer Point is unreliable, so a sale must be recordable with
 * no connection at all and must reach the server later, exactly once. That is
 * the whole job of this module.
 *
 * Design notes:
 *
 *  - The `clientTxnId` is generated **before the first attempt** and never
 *    changes. It is the idempotency key the server deduplicates on, so a retry
 *    after an ambiguous failure can never create a second sale.
 *  - Items are only removed once the server has confirmed them. A failure keeps
 *    the row, increments `attempts`, and records why.
 *  - Nothing here decides business outcomes. Stock availability, closed days and
 *    permissions are all re-checked server-side on sync (spec §88).
 *
 * ## Shared devices
 *
 * A Customer Point usually has one tablet behind the counter that three people
 * use in a day. Two things follow, and both are security properties rather than
 * conveniences:
 *
 *  1. **Every stored record is scoped** to the organisation, user and branch
 *    that created it, and every read filters on that scope. The next person to
 *    sign in cannot see the previous person's queued sales or cached customer
 *    data, because the queries never return them.
 *
 *  2. **Signing out clears cached reads but never pending writes.** Cached
 *    products and recent-sync confirmations are somebody's data and are wiped.
 *    An unsynced sale is somebody's *money* and is kept — flagged, attributed to
 *    the user who made it, and impossible to reattribute by signing in as
 *    someone else. Silently deleting it would lose a real transaction, which is
 *    the one outcome this whole subsystem exists to prevent.
 */
import Dexie, { type Table } from 'dexie';

export type QueueScope = 'sale' | 'expense' | 'customer_payment';

export type QueueStatus = 'pending' | 'syncing' | 'failed' | 'conflict';

/**
 * Who a stored record belongs to.
 *
 * Present on every row in every table. A missing field is treated as "belongs to
 * nobody", which means it is never returned to anybody.
 */
export type SessionScope = {
  organizationId: string;
  userId: string;
  branchId: string | null;
};

export type QueuedOperation = {
  /** Auto-increment local key. */
  id?: number;
  /** Stable idempotency key — the same value on every retry. */
  clientTxnId: string;
  scope: QueueScope;
  payload: unknown;
  /** Short human summary shown in the pending list, e.g. "Sale — ৳1,250". */
  summary: string;
  status: QueueStatus;
  attempts: number;
  lastError?: string;
  queuedAt: string;
  updatedAt: string;
  /** When the transaction actually happened, which fixes its business day. */
  occurredAt: string;
  organizationId: string;
  /** The user who created it. Never rewritten, whoever signs in next. */
  userId: string;
  userName?: string;
  branchId: string | null;
  deviceId: string;
};

/** A record that reached the server, kept briefly so the user can see it landed. */
export type SyncedRecord = {
  id?: number;
  clientTxnId: string;
  scope: QueueScope;
  summary: string;
  serverNumber?: string | undefined;
  syncedAt: string;
  organizationId: string;
  userId: string;
  /** The server accepted it into a day that had already been closed. */
  lateSync?: boolean;
};

/** Cached reference data so the sale screen still works with no connection. */
export type CachedProduct = {
  id: string;
  branchId: string;
  organizationId: string;
  name: string;
  code: string;
  type: string;
  /**
   * What the sale screen needs to put a cached product back on the right tab.
   *
   * Optional, and read defensively, because a device that cached its catalogue
   * before these existed must keep selling from it rather than lose the list:
   * `saleTabFor` files a row with no kind or category by its type alone, and
   * the next online load overwrites it with the full record.
   */
  kind?: string | null;
  categoryName?: string | null;
  isFavourite?: boolean;
  sellingPrice: string;
  trackStock: boolean;
  imeiTracked: boolean;
  available: number | null;
  cachedAt: string;
};

class CpcelDatabase extends Dexie {
  queue!: Table<QueuedOperation, number>;
  synced!: Table<SyncedRecord, number>;
  products!: Table<CachedProduct, string>;

  constructor() {
    super('cpcel');
    this.version(1).stores({
      queue: '++id, &clientTxnId, status, scope, queuedAt',
      synced: '++id, clientTxnId, syncedAt',
      products: 'id, branchId, name',
    });
    // Version 2 adds the ownership columns and indexes them, so that scoped
    // reads are a lookup rather than a full scan of the table.
    this.version(2)
      .stores({
        queue: '++id, &clientTxnId, status, scope, queuedAt, organizationId, userId, branchId, deviceId',
        synced: '++id, clientTxnId, syncedAt, organizationId, userId',
        products: 'id, branchId, name, organizationId',
      })
      .upgrade(async (tx) => {
        // Rows written before scoping existed cannot be attributed to anyone.
        // They keep their place in the queue — losing a real transaction is
        // never acceptable — but with an empty owner they are only visible to
        // the unattributed view, and no session can claim them as its own.
        await tx
          .table('queue')
          .toCollection()
          .modify((row: Partial<QueuedOperation>) => {
            row.organizationId ??= '';
            row.userId ??= '';
            row.branchId ??= null;
            row.deviceId ??= '';
            row.occurredAt ??= row.queuedAt ?? new Date().toISOString();
          });
        await tx.table('synced').clear();
        await tx.table('products').clear();
      });
  }
}

let instance: CpcelDatabase | null = null;

/** Lazily open the database — it only exists in a browser. */
export function offlineDb(): CpcelDatabase | null {
  if (typeof window === 'undefined' || typeof indexedDB === 'undefined') return null;
  instance ??= new CpcelDatabase();
  return instance;
}

// ---------------------------------------------------------------------------
// Device identity
// ---------------------------------------------------------------------------

const DEVICE_KEY = 'cpcel_device_id';

/**
 * This browser profile's device id.
 *
 * Generated once and kept in `localStorage`, so it survives sign-out — the point
 * is to identify the *device*, which does not change when the person using it
 * does. Not a fingerprint: it is a random value this application generated
 * about itself, readable by nobody else.
 */
export function deviceId(): string {
  if (typeof window === 'undefined') return '';
  try {
    const existing = window.localStorage.getItem(DEVICE_KEY);
    if (existing) return existing;
    const generated = crypto.randomUUID();
    window.localStorage.setItem(DEVICE_KEY, generated);
    return generated;
  } catch {
    // Private browsing with storage blocked. A per-session id is still better
    // than none: the queue works, it simply is not recognisable across reloads.
    return crypto.randomUUID();
  }
}

// ---------------------------------------------------------------------------
// Queue
// ---------------------------------------------------------------------------

export type EnqueueInput = {
  clientTxnId: string;
  scope: QueueScope;
  payload: unknown;
  summary: string;
  occurredAt?: string;
  userName?: string;
};

export async function enqueue(operation: EnqueueInput, scope: SessionScope): Promise<void> {
  const db = offlineDb();
  if (!db) return;
  const now = new Date().toISOString();
  await db.queue.add({
    ...operation,
    status: 'pending',
    attempts: 0,
    queuedAt: now,
    updatedAt: now,
    occurredAt: operation.occurredAt ?? now,
    organizationId: scope.organizationId,
    userId: scope.userId,
    branchId: scope.branchId,
    deviceId: deviceId(),
  });
}

/** True when the row belongs to the signed-in session. */
function belongsTo(row: { organizationId: string; userId: string }, scope: SessionScope): boolean {
  return row.organizationId === scope.organizationId && row.userId === scope.userId;
}

export async function pendingOperations(scope: SessionScope): Promise<QueuedOperation[]> {
  const db = offlineDb();
  if (!db) return [];
  const rows = await db.queue.where('status').anyOf('pending', 'failed').sortBy('queuedAt');
  return rows.filter((row) => belongsTo(row, scope));
}

export async function allOperations(scope: SessionScope): Promise<QueuedOperation[]> {
  const db = offlineDb();
  if (!db) return [];
  const rows = await db.queue.orderBy('queuedAt').toArray();
  return rows.filter((row) => belongsTo(row, scope));
}

export async function countPending(scope: SessionScope): Promise<number> {
  const db = offlineDb();
  if (!db) return 0;
  const rows = await db.queue.where('status').anyOf('pending', 'failed', 'syncing').toArray();
  return rows.filter((row) => belongsTo(row, scope)).length;
}

/**
 * Work left behind by somebody else on this device.
 *
 * Shown to the person signing out, and on the sync screen, so that unsynced
 * transactions belonging to another user are visible as a fact to be dealt
 * with rather than a silent surprise. Only counts and owners are exposed —
 * never the contents, which belong to that user.
 */
export async function foreignPendingSummary(
  scope: SessionScope,
): Promise<{ count: number; owners: string[] }> {
  const db = offlineDb();
  if (!db) return { count: 0, owners: [] };
  const rows = await db.queue.where('status').anyOf('pending', 'failed', 'syncing').toArray();
  const foreign = rows.filter((row) => !belongsTo(row, scope));
  const owners = [...new Set(foreign.map((row) => row.userName ?? 'another user'))];
  return { count: foreign.length, owners };
}

export async function markSyncing(ids: number[]): Promise<void> {
  const db = offlineDb();
  if (!db) return;
  await db.queue.where('id').anyOf(ids).modify({ status: 'syncing', updatedAt: new Date().toISOString() });
}

/**
 * Put back anything left mid-flight.
 *
 * `syncing` is a claim that a request is in the air right now. A tab closed
 * during a sync, a browser killed, a device that lost power — each leaves that
 * claim behind with nothing to honour it, and `pendingOperations` does not
 * return `syncing` rows, so the operation would never be attempted again. On a
 * queue whose entire purpose is that a sale is never lost, that is the worst
 * available outcome.
 *
 * Replaying is safe by construction: `clientTxnId` is fixed before the first
 * attempt, so a row the server did receive is recognised as a replay and
 * returns the original result rather than writing a second sale.
 *
 * Only rows older than `staleAfterMs` are reclaimed, so a sync genuinely in
 * flight in another tab is left alone.
 */
export async function reclaimStalledOperations(
  scope: SessionScope,
  staleAfterMs = 2 * 60 * 1000,
): Promise<number> {
  const db = offlineDb();
  if (!db) return 0;
  const rows = await db.queue.where('status').equals('syncing').toArray();
  const ids = stalledOperationIds(rows, scope, Date.now(), staleAfterMs);
  if (ids.length === 0) return 0;

  await db.queue.where('id').anyOf(ids).modify({
    status: 'pending',
    updatedAt: new Date().toISOString(),
    lastError: 'The previous attempt did not finish. Queued again.',
  });
  return ids.length;
}

/**
 * Which of the given `syncing` rows have been abandoned.
 *
 * Split out from the Dexie call so the rule — mine, and older than the
 * threshold — can be tested without a browser database.
 */
export function stalledOperationIds(
  rows: readonly QueuedOperation[],
  scope: SessionScope,
  now: number,
  staleAfterMs: number,
): number[] {
  const cutoff = new Date(now - staleAfterMs).toISOString();
  return rows
    .filter(
      (row) =>
        row.id !== undefined &&
        row.status === 'syncing' &&
        belongsTo(row, scope) &&
        row.updatedAt <= cutoff,
    )
    .map((row) => row.id as number);
}

export async function markSynced(
  operation: QueuedOperation,
  serverNumber: string | undefined,
  lateSync = false,
): Promise<void> {
  const db = offlineDb();
  if (!db || operation.id === undefined) return;
  await db.transaction('rw', db.queue, db.synced, async () => {
    await db.queue.delete(operation.id as number);
    await db.synced.add({
      clientTxnId: operation.clientTxnId,
      scope: operation.scope,
      summary: operation.summary,
      serverNumber,
      syncedAt: new Date().toISOString(),
      organizationId: operation.organizationId,
      userId: operation.userId,
      lateSync,
    });
  });
}

export async function markFailed(
  operation: QueuedOperation,
  reason: string,
  status: 'failed' | 'conflict' = 'failed',
): Promise<void> {
  const db = offlineDb();
  if (!db || operation.id === undefined) return;
  await db.queue.update(operation.id, {
    status,
    attempts: operation.attempts + 1,
    lastError: reason,
    updatedAt: new Date().toISOString(),
  });
}

/**
 * Discard a queued item the user has decided not to keep.
 *
 * Scoped: you can only discard your own work. Discarding is the one destructive
 * action here and it is always a deliberate choice by the person who created the
 * transaction, never something sign-out does on their behalf.
 */
export async function discardOperation(id: number, scope: SessionScope): Promise<boolean> {
  const db = offlineDb();
  if (!db) return false;
  const row = await db.queue.get(id);
  if (!row || !belongsTo(row, scope)) return false;
  await db.queue.delete(id);
  return true;
}

/** Put a failed or conflicted item back in line for another attempt. */
export async function retryOperation(id: number, scope: SessionScope): Promise<boolean> {
  const db = offlineDb();
  if (!db) return false;
  const row = await db.queue.get(id);
  if (!row || !belongsTo(row, scope)) return false;
  await db.queue.update(id, { status: 'pending', updatedAt: new Date().toISOString() });
  return true;
}

export async function recentlySynced(scope: SessionScope, limit = 10): Promise<SyncedRecord[]> {
  const db = offlineDb();
  if (!db) return [];
  const rows = await db.synced.orderBy('syncedAt').reverse().toArray();
  return rows.filter((row) => belongsTo(row, scope)).slice(0, limit);
}

/** Drop confirmations older than a day — they have served their purpose. */
export async function pruneSynced(): Promise<void> {
  const db = offlineDb();
  if (!db) return;
  const cutoff = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
  await db.synced.where('syncedAt').below(cutoff).delete();
}

// ---------------------------------------------------------------------------
// Cached reference data
// ---------------------------------------------------------------------------

export async function cacheProducts(
  branchId: string,
  products: Omit<CachedProduct, 'branchId' | 'organizationId' | 'cachedAt'>[],
  scope: SessionScope,
): Promise<void> {
  const db = offlineDb();
  if (!db) return;
  const cachedAt = new Date().toISOString();
  await db.products.bulkPut(
    products.map((p) => ({ ...p, branchId, organizationId: scope.organizationId, cachedAt })),
  );
}

export async function cachedProducts(branchId: string, scope: SessionScope): Promise<CachedProduct[]> {
  const db = offlineDb();
  if (!db) return [];
  const rows = await db.products.where('branchId').equals(branchId).toArray();
  // Tenant scope is re-applied on read, not only on write: a cache poisoned by
  // any means still cannot show one organisation's catalogue to another.
  return rows.filter((row) => row.organizationId === scope.organizationId);
}

// ---------------------------------------------------------------------------
// Sign-out
// ---------------------------------------------------------------------------

export type SignOutResult = {
  /** Cached read-only records removed. */
  clearedCached: number;
  /** Unsynced transactions deliberately kept, and who they belong to. */
  keptPending: number;
};

/**
 * Clear this device's cached data when a user signs out.
 *
 * Removes everything that is a *copy of something the server holds* — cached
 * products, sync confirmations — so the next person to use the tablet cannot
 * read the previous user's data. Keeps everything that is *the only copy* of
 * something: unsynced transactions stay, attributed to the user who created
 * them, and are reported back so the interface can say so plainly.
 */
export async function clearSessionData(scope: SessionScope): Promise<SignOutResult> {
  const db = offlineDb();
  if (!db) return { clearedCached: 0, keptPending: 0 };

  const [products, synced] = await Promise.all([
    db.products.where('organizationId').equals(scope.organizationId).toArray(),
    db.synced.toArray(),
  ]);

  const syncedMine = synced.filter((row) => belongsTo(row, scope));

  await db.transaction('rw', db.products, db.synced, async () => {
    await db.products.bulkDelete(products.map((p) => p.id));
    await db.synced.bulkDelete(
      syncedMine.map((row) => row.id).filter((id): id is number => id !== undefined),
    );
  });

  const keptPending = await countPending(scope);
  return { clearedCached: products.length + syncedMine.length, keptPending };
}
